import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ngxCsv } from "ngx-csv";
import { ClientWithStats } from "../../client/client.interface";
import {
  Employee,
  EmployeeListItem,
} from "../../employees/employees.interface";

@Injectable({ providedIn: "root" })
export class ReportsService {
  constructor(private translate: TranslateService) {}

  public generateClientsReport = (clients: ClientWithStats[]): void => {
    const reportData = clients.map(
      ({
        name,
        domain,
        createdAt,
        googleAdminEmail,
        stats,
        planId,
        trialFinishDate,
      }) => ({
        name: name || "",
        domain,
        createdAt,
        googleAdminEmail,
        installedCurrentWeek:
          stats.current.numberOfEmployeesWithSignatureInstalled,
        totalCurrentWeek: stats.current.numberOfEmployees,
        installedPreviousWeek:
          stats.previous.numberOfEmployeesWithSignatureInstalled,
        totalPreviousWeek: stats.previous.numberOfEmployees,
        planId,
        trialFinishDate: trialFinishDate || "",
      }),
    );

    const headers = [
      "name",
      "domain",
      "createdAt",
      "googleAdminEmail",
      "installedCurrentWeek",
      "totalCurrentWeek",
      "installedPreviousWeek",
      "totalPreviousWeek",
      "planId",
      "trialFinishDate",
    ];

    this.generateReport(reportData, headers, "menu.clients");
  };

  public generateEmployeesReport = (
    employees: (Employee | EmployeeListItem)[],
    employeeSettings: string[],
  ): void => {
    const groupedEmployees = employees.reduce(
      (acc, employee) => {
        if ("isAlias" in employee && employee.isAlias) {
          const owner = acc.find((e) => e.fullName === employee.fullName);
          if (owner) {
            owner.aliases = owner.aliases || [];
            owner.aliases.push(employee);
          }
        } else {
          acc.push({ ...(employee as EmployeeListItem), aliases: [] });
        }
        return acc;
      },
      [] as Array<
        EmployeeListItem & {
          aliases: EmployeeListItem[];
          dynamicVariables?: Record<string, unknown>;
        }
      >,
    );

    const reportData: Record<string, unknown>[] = [];

    groupedEmployees.forEach((owner) => {
      reportData.push({
        email: owner.email,
        givenName: owner.givenName || "",
        familyName: owner.familyName || "",
        typ: this.translate.instant("employee.employee"),
        excludedFromSignatures: owner.excludedFromSignatures,
        createdAt: owner.createdAt,
        modifiedAt: owner.modifiedAt,
        ...employeeSettings.reduce(
          (ac, settingName) => ({
            ...ac,
            [settingName]:
              (owner.dynamicVariables?.[settingName] as string) || "",
          }),
          {},
        ),
      });

      owner.aliases.forEach((alias) => {
        const [givenName, ...familyNameParts] = (alias.fullName || "").split(
          " ",
        );
        const familyName = familyNameParts.join(" ");
        reportData.push({
          email: alias.email || "",
          givenName: givenName || "",
          familyName: familyName || "",
          typ: this.translate.instant("employee.alias"),
          excludedFromSignatures: "",
          createdAt: "",
          modifiedAt: "",
          ...employeeSettings.reduce(
            (ac, settingName) => ({
              ...ac,
              [settingName]: "",
            }),
            {},
          ),
        });
      });
    });

    let headers = [
      "email",
      "givenName",
      "familyName",
      "type",
      "excludedFromSignatures",
      "createdAt",
      "modifiedAt",
      ...employeeSettings,
    ];

    if (reportData.length === 0) {
      headers = ["email", "givenName", "familyName", "phoneNumber", "jobTitle"];
    }

    this.generateReport(reportData, headers, "menu.employees");
  };

  private generateReport(
    reportData: Record<string, unknown>[],
    headers: string[],
    nameKey: string,
  ): void {
    new ngxCsv(reportData, this.translate.instant(nameKey) as string, {
      headers,
    });
  }
}
